import React from 'react';
import {
  FaBalanceScale,
  FaBreadSlice,
  FaCashRegister,
  FaChartLine,
  FaFileInvoiceDollar,
  FaGlassMartiniAlt,
  FaIceCream,
  FaMotorcycle,
  FaPizzaSlice,
  FaPlug,
  FaStoreAlt,
  FaTruck,
  FaUtensils,
  FaWarehouse,
} from 'react-icons/fa';
import ProductPageTemplate from '../../components/productPageTemplate';

const data = {
  theme: {
    accent: '#E85D8F',
    accentSoft: '#FF8BAF',
    darkBg: '#4A1E2D',
    darkBgSoft: '#A63F65',
  },
  hero: {
    eyebrows: ['Gestão para food service', 'Comandas, salão e delivery', 'PDV e fiscal integrados'],
    accent: 'Food',
    title: 'que organiza salão, delivery e caixa no mesmo ritmo.',
    lead:
      'O ECOSIS Food foi pensado para bares, restaurantes, pizzarias e operações de entrega que precisam acelerar atendimento sem perder controle.',
    support:
      'A solução conecta comandas, emissão, financeiro, estoque, painéis de impressão, entregador e meios de venda em um fluxo mais enxuto.',
    metrics: [
      'NFe e NFCe na rotina do food service',
      'Comandas, entrega e caixa em uma base só',
      'Operação do delivery à cozinha e à venda',
    ],
    primaryCta: { label: 'Ver módulos', href: '#modulos' },
    secondaryCta: { label: 'Falar com a equipe', href: '#contato-produto' },
    visualCards: [
      { label: 'Atendimento', value: 'Mais ágil' },
      { label: 'Salão e entrega', value: 'Mesmo fluxo' },
    ],
    panelTitle: 'Painel do atendimento',
    panelItems: [
      {
        label: 'Salão',
        title: 'Comandas e impressão de produção',
        meta: 'Painéis para cozinha e bar ajudam o atendimento a ganhar ritmo sem perder rastreabilidade.',
      },
      {
        label: 'Entrega',
        title: 'Controle do entregador e do pedido',
        meta: 'Delivery, cardápio digital, QR Code e frete por KM integrados à operação.',
      },
      {
        label: 'Caixa',
        title: 'Financeiro e PDV conectados',
        meta: 'A venda na ponta já alimenta emissão, caixa e controle operacional.',
      },
    ],
  },
  intro: {
    tag: 'Visão geral',
    title: 'Uma tela mais profissional para um produto que precisa ser rápido, direto e operacional.',
    lead:
      'A página anterior trazia os recursos certos, mas sem ritmo visual consistente. Agora a proposta do produto fica mais clara logo na primeira leitura.',
    cardTitle: 'Do pedido à entrega, sem quebrar o fluxo',
    cardText:
      'O ECOSIS Food foi estruturado para conectar atendimento, cozinha, entrega, emissão e financeiro em um mesmo caminho operacional, sem camadas desnecessárias.',
    quickPoints: [
      'Comandas, salão, balcão e delivery na mesma operação.',
      'Integrações importantes para venda digital, pagamento e comunicação.',
      'Leitura gerencial mais rápida para uma rotina de alto giro.',
    ],
  },
  modulesSection: {
    tag: 'Módulos principais',
    title: 'Os recursos centrais do Food agora seguem um padrão mais coeso com o restante do portfólio.',
    lead:
      'O conteúdo abaixo preserva as frentes que já estavam listadas, mas organiza melhor a leitura para quem está avaliando a solução.',
  },
  modules: [
    {
      icon: FaFileInvoiceDollar,
      title: 'Documentos fiscais',
      description: 'Emita documentos fiscais necessários para o food service sem deslocar a operação para outro sistema.',
      bullets: ['NFe e NFCe', 'Emissão ligada ao atendimento', 'Menos atrito na rotina'],
    },
    {
      icon: FaBalanceScale,
      title: 'Fiscal e contábil',
      description: 'A página original já destacava a cobertura contábil e fiscal para diferentes perfis operacionais.',
      bullets: ['Pessoa física, Lucro Real e Presumido', 'Sintegra e SPED', 'Contribuições, Bloco K e inventário'],
    },
    {
      icon: FaCashRegister,
      title: 'Frente de caixa e PDV',
      description: 'A ponta de venda acelera a operação e mantém caixa e emissão ligados ao que realmente foi atendido.',
      bullets: ['Controle de caixa', 'Emissão de NFCe', 'Venda rápida'],
    },
    {
      icon: FaUtensils,
      title: 'Gestão do negócio',
      description: 'Controle a rotina do salão e da entrega com recursos que atendem a operação real de food service.',
      bullets: ['Comandas', 'Painel do garçom e de impressão', 'Cardápio digital, QR Code e promoções'],
    },
    {
      icon: FaMotorcycle,
      title: 'Delivery e expedição',
      description: 'Administre o entregador e o fluxo de saída sem precisar abrir processos paralelos para a entrega.',
      bullets: ['Controle do entregador', 'Frete por KM', 'Operação integrada ao pedido'],
    },
    {
      icon: FaWarehouse,
      title: 'Estoque e produção',
      description: 'Controle insumos, produção e etiquetagem com mais visibilidade para uma operação de alto giro.',
      bullets: ['Controle de produção', 'Emissão de etiquetas', 'Relação direta com vendas e comandas'],
    },
    {
      icon: FaChartLine,
      title: 'Gestão inteligente',
      description: 'Use dashboards, relatórios e gestão documental para acelerar a leitura da operação.',
      bullets: ['Dashboards fáceis de interpretar', 'Relatórios gerenciais', 'Gestão e assinatura de documentos'],
    },
    {
      icon: FaPlug,
      title: 'Integrações',
      description: 'Conecte o produto a canais e meios de pagamento importantes para a rotina do setor.',
      bullets: ['iFood e Anota AI', 'PIX, TEF e WhatsApp', 'SEUPOS e Connect'],
    },
  ],
  operations: {
    tag: 'Onde encaixa melhor',
    title: 'O produto cobre do atendimento de salão à operação de entrega.',
    lead:
      'Esse bloco resume os segmentos atendidos e reforça os recursos complementares que tornam a rotina mais fluida.',
    audienceTitle: 'Perfis atendidos',
    audienceText:
      'O ECOSIS Food foi desenhado para negócios de alimentação com volume operacional, necessidade de agilidade e pouco espaço para retrabalho.',
    sectors: [
      { icon: FaGlassMartiniAlt, label: 'Bares' },
      { icon: FaUtensils, label: 'Restaurantes' },
      { icon: FaPizzaSlice, label: 'Pizzarias' },
      { icon: FaMotorcycle, label: 'Delivery' },
      { icon: FaBreadSlice, label: 'Padarias' },
      { icon: FaIceCream, label: 'Docerias' },
      { icon: FaStoreAlt, label: 'Lanchonetes' },
      { icon: FaTruck, label: 'Trailers' },
    ],
    stat: {
      value: 'Fluxo',
      text: 'Atende do delivery à cozinha e à venda, mantendo o caixa e a emissão conectados à operação.',
    },
    cards: [
      {
        title: 'Painel de produção',
        text: 'A operação de cozinha e bar ganha cadência com painéis de impressão conectados à comanda.',
        bullets: ['Bar e cozinha no mesmo fluxo', 'Menos ruído no atendimento'],
      },
      {
        title: 'Venda digital',
        text: 'O produto suporta cardápio digital, QR Code e canais externos para ampliar pedidos sem perder controle.',
        bullets: ['Cardápio digital', 'QR Code', 'Promoções e venda conectada'],
      },
      {
        title: 'Meios de pagamento',
        text: 'SEUPOS e Connect aparecem como complementos importantes para frente de caixa e pagamentos.',
        bullets: ['Stone, Rede e Mercado Pago', 'Vero e Cielo no Connect'],
      },
      {
        title: 'Financeiro ligado ao atendimento',
        text: 'Contas, conciliação bancária e boletos ficam mais coerentes quando nascem da operação correta.',
        bullets: ['Contas a pagar e receber', 'Conciliação bancária', 'Menos fechamento manual'],
      },
    ],
  },
  contact: {
    tag: 'Contato',
    title: 'Quer avaliar o ECOSIS Food para salão, balcão ou delivery?',
    lead: 'A equipe pode entender seu fluxo atual e indicar a melhor composição de operação, caixa e atendimento.',
  },
};

function EcosisFood() {
  return <ProductPageTemplate data={data} />;
}

export default EcosisFood;
